import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { quotesAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { CountryCombobox } from '../ui/country-combobox';
import { zoneFieldFor } from '../../lib/destinationZones';

// A booking made by staff on a customer's behalf. The server prices it exactly
// as the customer's own booking would be priced - from the quote, with their
// discount - and saves it into their account. Admins can change the price
// (with a reason) and record or take payment; anyone who can book can leave it
// to be paid later and send the customer a payment link.

const OFFLINE_METHODS = [
  ['cash', 'Cash'],
  ['bank_transfer', 'Bank transfer'],
  ['upi', 'UPI'],
  ['cheque', 'Cheque'],
  ['other', 'Other'],
];

const SERVICE_TYPES = ['standard', 'express', 'overnight', 'economy', 'same_day'];

const EMPTY_ADDRESS = {
  name: '', company: '', phone: '', email: '', street: '', landmark: '', city: '', state: '', postal_code: '',
};
const ADDRESS_KEYS = Object.keys(EMPTY_ADDRESS);

// "United Kingdom", "UNITED_KINGDOM" and "united-kingdom" compare equal.
const countryKey = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const EMPTY_ROUTE = {
  to: '', toPostcode: '', type: 'parcel',
  weight: '', length: '', width: '', height: '', value: '', contents: '',
};
const PRICED_FIELDS = ['to', 'toPostcode', 'type', 'weight', 'length', 'width', 'height'];

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const inputClass =
  'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none';
const labelClass = 'block text-xs font-medium text-gray-600';

const Section = ({ step, title, children, right }) => (
  <section className="rounded-lg bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-gray-900">
        <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-700">
          {step}
        </span>
        {title}
      </h3>
      {right}
    </div>
    {children}
  </section>
);

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const AddressFields = ({ value, onChange, prefix }) => {
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Name"><input data-test={`${prefix}-name`} className={inputClass} value={value.name} onChange={set('name')} /></Field>
      <Field label="Phone"><input data-test={`${prefix}-phone`} className={inputClass} value={value.phone} onChange={set('phone')} /></Field>
      <Field label="Company (optional)"><input className={inputClass} value={value.company} onChange={set('company')} /></Field>
      <Field label="Email (optional)">
        <input data-test={`${prefix}-email`} type="email" className={inputClass} value={value.email} onChange={set('email')} />
      </Field>
      <Field label="Address" className="col-span-2">
        <input data-test={`${prefix}-street`} className={inputClass} value={value.street} onChange={set('street')} />
      </Field>
      <Field label="Landmark (optional)"><input className={inputClass} value={value.landmark} onChange={set('landmark')} /></Field>
      <Field label="City"><input data-test={`${prefix}-city`} className={inputClass} value={value.city} onChange={set('city')} /></Field>
      <Field label="State (optional)"><input data-test={`${prefix}-state`} className={inputClass} value={value.state} onChange={set('state')} /></Field>
      <Field label="Postal code"><input data-test={`${prefix}-postal`} className={inputClass} value={value.postal_code} onChange={set('postal_code')} /></Field>
    </div>
  );
};

// Fill an address from the customer's address book.
const SavedAddressPicker = ({ entries, onPick, testId }) => {
  if (!entries.length) return null;
  return (
    <select
      data-test={testId}
      className="max-w-[16rem] rounded-md border border-gray-300 px-2 py-1 text-xs"
      value=""
      onChange={(e) => {
        const picked = entries.find((a) => a.id === e.target.value);
        if (picked) onPick(picked);
      }}
    >
      <option value="">Fill from address book...</option>
      {entries.map((a) => (
        <option key={a.id} value={a.id}>
          {[a.label || a.name, a.city].filter(Boolean).join(' - ')}
        </option>
      ))}
    </select>
  );
};

const fromBook = (a) => Object.fromEntries(ADDRESS_KEYS.map((k) => [k, a[k] || '']));

// What the server's Address model takes; empty optional fields are left out.
const toAddress = (a, country) => ({
  name: a.name.trim(),
  company: a.company.trim() || null,
  phone: a.phone.trim(),
  email: a.email.trim() || null,
  street: a.street.trim(),
  landmark: a.landmark.trim() || null,
  city: a.city.trim(),
  state: a.state.trim() || null,
  postal_code: a.postal_code.trim(),
  country,
});

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) {
    const where = (detail[0].loc || []).slice(-1)[0];
    return where ? `${detail[0].msg} (${where})` : detail[0].msg;
  }
  return fallback;
};

const AdminCreateBooking = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canOverride = hasPermission('override_booking_price');
  const canRecord = hasPermission('record_payments');

  // Customer
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [addressBook, setAddressBook] = useState([]);
  const searchSeq = useRef(0);
  const previewSeq = useRef(0);

  // Shipment and rates
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [route, setRoute] = useState(EMPTY_ROUTE);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Addresses
  const [sender, setSender] = useState(EMPTY_ADDRESS);
  const [recipient, setRecipient] = useState(EMPTY_ADDRESS);
  const [notes, setNotes] = useState('');

  // Price and payment
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [override, setOverride] = useState({ enabled: false, amount: '', reason: '' });
  // Door pickup (charged by the sender's PIN code) or drop-off at the office.
  const [pickupMethod, setPickupMethod] = useState('pickup');
  const [payment, setPayment] = useState({ method: 'pay_later', sendLink: true, offlineMethod: 'cash', reference: '' });

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    quotesAPI.getPriorityConnectionsCountries()
      .then((data) => setCountries(data?.countries || []))
      .catch(() => setCountries([]))
      .finally(() => setLoadingCountries(false));
  }, []);

  // Customer search, debounced; a slow early reply never overwrites a later one.
  useEffect(() => {
    const q = query.trim();
    if (customer || q.length < 2) {
      setResults([]);
      return undefined;
    }
    const seq = ++searchSeq.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/admin/booking-customers', { params: { q } });
        if (seq === searchSeq.current) setResults(res.data?.data || []);
      } catch {
        if (seq === searchSeq.current) setResults([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, customer]);

  const pickCustomer = (c) => {
    setCustomer(c);
    setResults([]);
    setQuery('');
    setAddressBook([]);
    setSender((s) => ({
      ...s,
      name: s.name || c.name || '',
      company: s.company || c.company || '',
      phone: s.phone || c.phone || '',
      email: s.email || c.email || '',
    }));
    setPreview(null);
    api.get(`/admin/booking-customers/${c.id}/addresses`)
      .then((res) => setAddressBook(res.data?.data || []))
      .catch(() => setAddressBook([]));
  };

  const changeCustomer = () => {
    setCustomer(null);
    setAddressBook([]);
    setPreview(null);
    if (payment.method !== 'pay_later') setPayment((p) => ({ ...p, method: 'pay_later' }));
  };

  // Rates are for one destination, type and size; changing any of them means
  // the rates on screen no longer apply.
  const setRouteField = (changes) => {
    setRoute((r) => ({ ...r, ...changes }));
    if (Object.keys(changes).some((k) => PRICED_FIELDS.includes(k))) {
      previewSeq.current += 1;
      setQuote(null);
      setSelected(null);
      setPreview(null);
      setPreviewError(null);
    }
  };

  const zone = zoneFieldFor(route.to);
  const isDoc = route.type === 'document';
  const weight = isDoc ? 0.5 : (num(route.weight) || 0.5);
  const dims = {
    length: num(route.length) || (isDoc ? 21 : 30),
    width: num(route.width) || (isDoc ? 29.7 : 20),
    height: num(route.height) || (isDoc ? 1 : 10),
  };

  const getRates = async () => {
    previewSeq.current += 1;
    setQuoteError(null);
    setSelected(null);
    setPreview(null);
    setPreviewError(null);
    if (!route.to) {
      setQuoteError('Choose the destination country.');
      return;
    }
    if (zone && !route.toPostcode.trim()) {
      setQuoteError(`${zone.label} is needed to price this destination.`);
      return;
    }
    setQuoting(true);
    try {
      const value = parseFloat(route.value) || (isDoc ? 500 : 100);
      const data = await quotesAPI.createQuote({
        from_country: 'IN',
        to_country: route.to,
        to_postal_code: route.toPostcode || null,
        weight,
        shipment_type: route.type,
        declared_value: parseFloat(route.value) || 0,
        length: num(route.length),
        width: num(route.width),
        height: num(route.height),
        packages: [{ id: 1, weight, ...dims, quantity: 1 }],
        content_items: [{ id: 1, description: route.contents || (isDoc ? 'Documents' : 'General Items'), quantity: 1, value, hsn_code: '' }],
      });
      setQuote(data);
      if (!data?.carrier_quotes?.length) setQuoteError('No carrier quoted this route. Check the destination and weight.');
      // The zone is part of the recipient's address; fill it in if still empty.
      // A Canadian zone is only the first part of a postcode, so it is not used.
      const z = route.toPostcode.trim();
      if (zone?.options) {
        setRecipient((r) => ({ ...r, state: r.state || z }));
      } else if (zone && zone.mode === 'numeric' && z.length === zone.maxLength) {
        setRecipient((r) => ({ ...r, postal_code: r.postal_code || z }));
      }
    } catch (err) {
      setQuote(null);
      setQuoteError(errorText(err, 'Could not get rates. Please try again.'));
    } finally {
      setQuoting(false);
    }
  };

  const buildBooking = (carrier = selected) => ({
    quote_id: quote?.id,
    sender: toAddress(sender, 'India'),
    recipient: toAddress(recipient, route.to),
    package_info: {
      type: route.type,
      dimensions: { ...dims, weight },
      declared_value: parseFloat(route.value) || 0,
      contents_description: route.contents.trim() || (isDoc ? 'Documents' : 'General items'),
    },
    carrier_name: carrier?.carrier_name,
    service_type: SERVICE_TYPES.includes(carrier?.service_level) ? carrier.service_level : 'standard',
    updated_pricing: { original_cost: carrier?.total_cost },
    pickup_method: pickupMethod,
    notes: notes.trim() || null,
  });

  // The server's price for this customer, before anything is saved.
  const loadPreview = async (carrier) => {
    const seq = ++previewSeq.current;
    setPreview(null);
    setPreviewError(null);
    if (!customer || !carrier) return;
    try {
      const res = await api.post('/admin/bookings/preview', { customer_id: customer.id, booking: buildBooking(carrier) });
      if (seq === previewSeq.current) setPreview(res.data?.data || null);
    } catch (err) {
      if (seq === previewSeq.current) setPreviewError(errorText(err, 'Could not price this booking.'));
    }
  };

  const pickCarrier = (cq) => {
    setSelected(cq);
    loadPreview(cq);
  };

  // A different customer has a different discount: price the choice again.
  useEffect(() => {
    if (selected) loadPreview(selected);
  }, [customer]);

  // The pickup charge follows the pickup PIN code and the pickup/drop-off
  // choice, so either change prices the booking again.
  useEffect(() => {
    if (!selected) return undefined;
    const timer = setTimeout(() => loadPreview(selected), 400);
    return () => clearTimeout(timer);
  }, [pickupMethod, sender.postal_code]);

  // Pickups are in India; a recipient is offered only for the chosen destination.
  const destination = countries.find((c) => c.code === route.to);
  const destinationKeys = new Set(
    [route.to, destination?.name, destination?.countryName].filter(Boolean).map(countryKey),
  );
  const senderBook = addressBook.filter(
    (a) => a.use_for !== 'recipient' && (!a.country || ['INDIA', 'IN'].includes(countryKey(a.country))),
  );
  const recipientBook = route.to
    ? addressBook.filter((a) => a.use_for !== 'sender' && destinationKeys.has(countryKey(a.country)))
    : [];

  const price = preview?.final_cost ?? null;
  const finalPrice = override.enabled && num(override.amount) ? num(override.amount) : price;
  const wallet = customer?.wallet;
  const walletShort = wallet && finalPrice != null && wallet.balance < finalPrice;

  const missing = () => {
    if (!customer) return 'Choose the customer.';
    if (!selected) return 'Get rates and choose a carrier.';
    for (const [who, a] of [['Sender', sender], ['Recipient', recipient]]) {
      for (const [key, label] of [['name', 'name'], ['phone', 'phone'], ['street', 'address'], ['city', 'city'], ['postal_code', 'postal code']]) {
        if (!String(a[key] || '').trim()) return `${who} ${label} is missing.`;
      }
    }
    if (!route.contents.trim() && !isDoc) return 'Describe the contents.';
    // Pickup is not checked here: the preview refreshes a moment after the PIN
    // code changes and may be stale, and the server refuses pickup it cannot
    // price with the exact reason anyway.
    if (override.enabled) {
      if (!num(override.amount)) return 'Enter the price to charge.';
      if (override.reason.trim().length < 3) return 'Give a reason for changing the price.';
    }
    if (payment.method === 'wallet' && (!wallet || !wallet.usable)) return "The customer's wallet cannot be used.";
    if (payment.method === 'wallet' && walletShort) return "The customer's wallet balance is not enough.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = missing();
    if (problem) {
      setSubmitError(problem);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        customer_id: customer.id,
        booking: buildBooking(),
        payment: {
          method: payment.method,
          offline_method: payment.method === 'offline' ? payment.offlineMethod : null,
          reference: payment.method === 'offline' ? (payment.reference.trim() || null) : null,
          send_payment_link: payment.method === 'pay_later' && payment.sendLink,
        },
      };
      if (override.enabled) body.price_override = { amount: num(override.amount), reason: override.reason.trim() };
      const res = await api.post('/admin/bookings/create', body);
      setResult({ ...res.data.data, warnings: res.data.warnings || [] });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(errorText(err, 'The booking could not be created. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    setResult(null);
    setCustomer(null);
    setAddressBook([]);
    setRoute(EMPTY_ROUTE);
    setQuote(null);
    setSelected(null);
    setSender(EMPTY_ADDRESS);
    setRecipient(EMPTY_ADDRESS);
    setNotes('');
    setPreview(null);
    setOverride({ enabled: false, amount: '', reason: '' });
    setPickupMethod('pickup');
    setPayment({ method: 'pay_later', sendLink: true, offlineMethod: 'cash', reference: '' });
    setSubmitError(null);
    setCopied(false);
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (result) {
    const link = result.payment_link;
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">Booking created</h2>
          <p className="mt-1 text-sm text-gray-600">It is in the customer's account now.</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-gray-500">Booking</dt><dd data-test="result-number" className="font-semibold">{result.shipment_number}</dd></div>
            <div><dt className="text-gray-500">AWB</dt><dd className="font-semibold">{result.awb || 'To be assigned'}</dd></div>
            <div><dt className="text-gray-500">Carrier</dt><dd>{result.carrier}</dd></div>
            <div><dt className="text-gray-500">Price</dt><dd className="font-semibold">{inr(result.final_cost)}</dd></div>
            <div className="col-span-2">
              <dt className="text-gray-500">Payment</dt>
              <dd data-test="result-payment" className={result.payment_status === 'paid' ? 'font-semibold text-green-700' : 'font-semibold text-amber-700'}>
                {result.payment_status === 'paid' ? 'Paid' : 'Awaiting payment'}
              </dd>
            </div>
          </dl>
          {result.price_override && (
            <p className="mt-3 rounded-md bg-orange-50 p-3 text-xs text-orange-800">
              Price changed from {inr(result.price_override.quoted_price)} to {inr(result.price_override.price)}: {result.price_override.reason}
            </p>
          )}
          {link?.short_url && (
            <div className="mt-4 rounded-md border border-gray-200 p-3">
              <p className="text-xs text-gray-600">
                Payment link {link.resent ? 'sent again' : 'sent'} to the customer by email and SMS:
              </p>
              <div className="mt-1 flex items-center gap-2">
                <code data-test="result-link" className="flex-1 truncate text-sm text-gray-900">{link.short_url}</code>
                <button type="button" onClick={() => copyLink(link.short_url)} className="rounded-md border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50">
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}
          {result.warnings?.length > 0 && (
            <ul className="mt-4 space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {result.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => navigate('/admin/bookings')} className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
              Go to bookings
            </button>
            <button type="button" onClick={startOver} className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700">
              Book another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      // Enter in a field must not create (and possibly charge for) a booking;
      // only the button does.
      onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}
      className="mx-auto max-w-4xl space-y-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">New booking for a customer</h2>
          <p className="text-xs text-gray-500">
            Priced as the customer's own booking, with their discount, and saved into their account.
          </p>
        </div>
        <button type="button" onClick={() => navigate('/admin/bookings')} className="text-sm text-gray-500 hover:text-gray-800">
          Cancel
        </button>
      </div>

      <Section step="1" title="Customer">
        {customer ? (
          <div className="flex items-start justify-between rounded-md border border-orange-200 bg-orange-50 p-3">
            <div className="text-sm">
              <p data-test="picked-customer" className="font-semibold text-gray-900">{customer.name || customer.email}</p>
              <p className="text-gray-600">{customer.email}{customer.phone ? ` · ${customer.phone}` : ''}</p>
              <p className="mt-1 text-xs text-gray-500">
                {customer.discount_percentage > 0 ? `${customer.discount_percentage}% discount applies` : 'No discount'}
                {wallet ? ` · Wallet ${inr(wallet.balance)}${wallet.usable ? '' : ' (not usable)'}` : ''}
              </p>
            </div>
            <button type="button" onClick={changeCustomer} className="text-xs text-orange-700 hover:underline">
              Change
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              data-test="customer-search"
              className={inputClass}
              placeholder="Search by name, email or phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {searching && <p className="mt-1 text-xs text-gray-400">Searching...</p>}
            {results.length > 0 && (
              <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                {results.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={!c.is_active}
                      onClick={() => pickCustomer(c)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-orange-50 disabled:opacity-50"
                    >
                      <span className="font-medium text-gray-900">{c.name || c.email}</span>
                      <span className="ml-2 text-gray-500">{c.email}{c.phone ? ` · ${c.phone}` : ''}</span>
                      {!c.is_active && <span className="ml-2 text-xs text-red-600">deactivated</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">No customer matches. Create their login in Users first.</p>
            )}
          </div>
        )}
      </Section>

      <Section step="2" title="Shipment and rates">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field label="Type">
            <select
              data-test="ship-type"
              className={inputClass}
              value={route.type}
              onChange={(e) => setRouteField({ type: e.target.value })}
            >
              <option value="parcel">Parcel</option>
              <option value="document">Document</option>
            </select>
          </Field>
          <Field label="To" className="col-span-1 md:col-span-3">
            <CountryCombobox
              id="admin-booking-to"
              countries={countries}
              loading={loadingCountries}
              value={route.to}
              onChange={(code) => setRouteField({ to: code, toPostcode: '' })}
              placeholder="Destination"
            />
          </Field>
          {zone && (
            <Field label={zone.label} className="col-span-2 md:col-span-4">
              {zone.options ? (
                <CountryCombobox
                  id="admin-booking-zone"
                  countries={zone.options.map(([code, name]) => ({ code, name }))}
                  value={route.toPostcode}
                  onChange={(code) => setRouteField({ toPostcode: code })}
                  placeholder="Search a state"
                />
              ) : (
                <input
                  className={inputClass}
                  inputMode={zone.mode}
                  maxLength={zone.maxLength}
                  placeholder={zone.placeholder}
                  value={route.toPostcode}
                  onChange={(e) => setRouteField({ toPostcode: e.target.value })}
                />
              )}
              <p className="mt-1 text-[11px] text-gray-400">{zone.hint}</p>
            </Field>
          )}
          {!isDoc && (
            <>
              <Field label="Weight (kg)"><input data-test="ship-weight" className={inputClass} inputMode="decimal" value={route.weight} onChange={(e) => setRouteField({ weight: e.target.value })} /></Field>
              <Field label="Length (cm)"><input className={inputClass} inputMode="decimal" value={route.length} onChange={(e) => setRouteField({ length: e.target.value })} /></Field>
              <Field label="Width (cm)"><input className={inputClass} inputMode="decimal" value={route.width} onChange={(e) => setRouteField({ width: e.target.value })} /></Field>
              <Field label="Height (cm)"><input className={inputClass} inputMode="decimal" value={route.height} onChange={(e) => setRouteField({ height: e.target.value })} /></Field>
            </>
          )}
          <Field label="Declared value (₹)"><input className={inputClass} inputMode="decimal" value={route.value} onChange={(e) => setRouteField({ value: e.target.value })} /></Field>
          <Field label="Contents" className="col-span-1 md:col-span-3">
            <input data-test="ship-contents" className={inputClass} placeholder={isDoc ? 'Documents' : 'What is in the parcel'} value={route.contents} onChange={(e) => setRouteField({ contents: e.target.value })} />
          </Field>
        </div>
        <div className="mt-4">
          <button
            type="button"
            data-test="get-rates"
            onClick={getRates}
            disabled={quoting}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
          >
            {quoting ? 'Getting rates...' : quote ? 'Get fresh rates' : 'Get rates'}
          </button>
          {quoteError && <p className="mt-2 text-sm text-red-600" role="alert">{quoteError}</p>}
        </div>
        {quote?.carrier_quotes?.length > 0 && (
          <div className="mt-4 grid gap-2 md:grid-cols-2">
            {quote.carrier_quotes.map((cq, i) => {
              const active = selected && selected.carrier_name === cq.carrier_name && selected.total_cost === cq.total_cost;
              return (
                <button
                  key={`${cq.carrier_name}-${i}`}
                  type="button"
                  data-test="carrier-option"
                  onClick={() => pickCarrier(cq)}
                  className={`rounded-md border p-3 text-left text-sm ${active ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-200' : 'border-gray-200 hover:border-orange-300'}`}
                >
                  <span className="font-semibold text-gray-900">{cq.carrier_name}</span>
                  {cq.service_level && <span className="ml-2 text-xs uppercase text-gray-500">{cq.service_level}</span>}
                  <span className="mt-1 block text-gray-700">
                    {inr(cq.total_cost)}
                    {customer?.discount_percentage > 0 && <span className="ml-1 text-xs text-gray-500">before {customer.discount_percentage}% discount</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Section>

      <Section
        step="3"
        title="Sender (pickup in India)"
        right={<SavedAddressPicker entries={senderBook} testId="sender-book" onPick={(a) => setSender(fromBook(a))} />}
      >
        <AddressFields value={sender} onChange={setSender} prefix="sender" />
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" name="pickupMethod" data-test="admin-pickup" checked={pickupMethod === 'pickup'}
              onChange={() => setPickupMethod('pickup')} />
            Door pickup from this address (charged by PIN code)
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="pickupMethod" data-test="admin-dropoff" checked={pickupMethod === 'drop_off'}
              onChange={() => setPickupMethod('drop_off')} />
            Customer drops it at our office (free)
          </label>
        </div>
      </Section>

      <Section
        step="4"
        title={destination ? `Recipient in ${destination.name || destination.countryName}` : 'Recipient'}
        right={<SavedAddressPicker entries={recipientBook} testId="recipient-book" onPick={(a) => setRecipient(fromBook(a))} />}
      >
        <AddressFields value={recipient} onChange={setRecipient} prefix="recipient" />
        <Field label="Notes for the team (optional)" className="mt-3">
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </Section>

      <Section step="5" title="Price and payment">
        <div className="rounded-md bg-gray-50 p-3 text-sm">
          {!selected && <p className="text-gray-500">Choose a carrier to see the price.</p>}
          {selected && !customer && <p className="text-gray-500">Choose the customer to see their price.</p>}
          {selected && customer && !preview && !previewError && <p className="text-gray-500">Pricing...</p>}
          {previewError && <p className="text-red-600" role="alert">{previewError}</p>}
          {preview && (
            <div data-test="price-preview" className="space-y-0.5">
              <p className="flex justify-between"><span className="text-gray-500">Quoted</span><span>{inr(preview.base_cost)}</span></p>
              {preview.discount_amount > 0 && (
                <p className="flex justify-between"><span className="text-gray-500">Customer discount ({preview.discount_percentage}%)</span><span>-{inr(preview.discount_amount)}</span></p>
              )}
              {preview.pickup_method === 'drop_off' ? (
                <p className="flex justify-between"><span className="text-gray-500">Drop-off at our office</span><span>Free</span></p>
              ) : preview.pickup_problem ? (
                <p className="text-red-600" role="alert" data-test="pickup-problem">{preview.pickup_problem}</p>
              ) : (
                <p className="flex justify-between">
                  <span className="text-gray-500">
                    Door pickup{preview.pickup_weight ? ` (${preview.pickup_weight} kg × ₹${preview.pickup_rate}/kg)` : ''}
                  </span>
                  <span data-test="pickup-charge">{inr(preview.pickup_charge)}</span>
                </p>
              )}
              <p className="flex justify-between font-semibold text-gray-900"><span>Price</span><span data-test="price-final">{inr(preview.final_cost)}</span></p>
            </div>
          )}
        </div>

        {canOverride && preview && (
          <div className="mt-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" data-test="override-toggle" checked={override.enabled} onChange={(e) => setOverride({ ...override, enabled: e.target.checked })} />
              Charge a different price
            </label>
            {override.enabled && (
              <div className="mt-2 grid grid-cols-3 gap-3">
                <Field label="Price to charge (₹)">
                  <input data-test="override-amount" className={inputClass} inputMode="decimal" value={override.amount} onChange={(e) => setOverride({ ...override, amount: e.target.value })} />
                </Field>
                <Field label="Reason (kept on the booking)" className="col-span-2">
                  <input data-test="override-reason" className={inputClass} value={override.reason} onChange={(e) => setOverride({ ...override, reason: e.target.value })} />
                </Field>
                {num(override.amount) && price != null && (
                  <p className="col-span-3 text-xs text-gray-600">
                    {num(override.amount) < price ? 'Discount' : 'Increase'} of {inr(Math.abs(num(override.amount) - price))} on the quoted {inr(price)}.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-4 space-y-2">
          <p className={labelClass}>How is it paid?</p>
          <label className="flex items-start gap-2 rounded-md border border-gray-200 p-3 text-sm">
            <input type="radio" name="pay" data-test="pay-later" checked={payment.method === 'pay_later'} onChange={() => setPayment({ ...payment, method: 'pay_later' })} />
            <span className="flex-1">
              <span className="font-medium text-gray-900">Pay later</span>
              <span className="block text-xs text-gray-500">The booking is confirmed now and paid afterwards.</span>
              {payment.method === 'pay_later' && (
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" data-test="send-link" checked={payment.sendLink} onChange={(e) => setPayment({ ...payment, sendLink: e.target.checked })} />
                  Send the customer a payment link by email and SMS
                </label>
              )}
            </span>
          </label>
          {canRecord && (
            <label className="flex items-start gap-2 rounded-md border border-gray-200 p-3 text-sm">
              <input type="radio" name="pay" data-test="pay-offline" checked={payment.method === 'offline'} onChange={() => setPayment({ ...payment, method: 'offline' })} />
              <span className="flex-1">
                <span className="font-medium text-gray-900">Already paid to us</span>
                <span className="block text-xs text-gray-500">Cash, bank transfer, UPI or cheque received directly.</span>
                {payment.method === 'offline' && (
                  <span className="mt-2 grid grid-cols-2 gap-3">
                    <select data-test="offline-method" className={inputClass} value={payment.offlineMethod} onChange={(e) => setPayment({ ...payment, offlineMethod: e.target.value })}>
                      {OFFLINE_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <input data-test="offline-reference" className={inputClass} placeholder="Reference (optional)" value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} />
                  </span>
                )}
              </span>
            </label>
          )}
          {canRecord && (
            <label className={`flex items-start gap-2 rounded-md border border-gray-200 p-3 text-sm ${!wallet?.usable ? 'opacity-60' : ''}`}>
              <input type="radio" name="pay" data-test="pay-wallet" disabled={!wallet?.usable} checked={payment.method === 'wallet'} onChange={() => setPayment({ ...payment, method: 'wallet' })} />
              <span className="flex-1">
                <span className="font-medium text-gray-900">From the customer's wallet</span>
                <span className="block text-xs text-gray-500">
                  {wallet ? `Balance ${inr(wallet.balance)}` : 'Choose a customer first.'}
                  {walletShort ? ' - not enough for this booking.' : ''}
                  {wallet && !wallet.usable ? ' - wallet is frozen or disabled.' : ''}
                </span>
              </span>
            </label>
          )}
        </div>

        {submitError && <p className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{submitError}</p>}

        <div className="mt-5 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {finalPrice != null && <>To charge: <span data-test="to-charge" className="font-semibold text-gray-900">{inr(finalPrice)}</span></>}
          </p>
          <button
            type="submit"
            data-test="create-booking"
            disabled={submitting}
            className="rounded-md bg-orange-600 px-5 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-60"
          >
            {submitting ? 'Creating...' : 'Create booking'}
          </button>
        </div>
      </Section>
    </form>
  );
};

export default AdminCreateBooking;
